import { supabase } from '../lib/supabase'
import { supabaseError } from '../lib/errors'

export type PhoneCallLogState = 'active' | 'archived' | 'spam' | 'deleted'

export type HandlingStatus = 'open' | 'assigned' | 'waiting' | 'resolved'

export interface PhoneCallLog {
  id: string
  call_id: string | null
  phone_number: string
  direction: 'incoming' | 'outbound' | null
  duration_seconds: number | null
  status: string | null
  caller_name: string | null
  line_number: string | null
  notes: string | null
  customer_id: string | null
  property_id: string | null
  contract_id: string | null
  job_id: string | null
  lead_id?: string | null
  proposal_id?: string | null
  conversation_id?: string | null
  assigned_to_user_id?: string | null
  assigned_at?: string | null
  assigned_by_user_id?: string | null
  handling_status?: HandlingStatus | null
  waiting_until?: string | null
  resolved_at?: string | null
  resolved_by?: string | null
  priority?: string | null
  queue_id?: string | null
  sla_due_at?: string | null
  state?: PhoneCallLogState | null
  state_changed_at?: string | null
  source: 'telebroad' | 'manual'
  created_by: string | null
  created_at: string
  updated_at: string
}

export type PhoneCallLogUpdate = Partial<
  Pick<
    PhoneCallLog,
    | 'notes'
    | 'customer_id'
    | 'lead_id'
    | 'proposal_id'
    | 'property_id'
    | 'contract_id'
    | 'job_id'
    | 'caller_name'
    | 'direction'
    | 'phone_number'
    | 'duration_seconds'
  >
>

export interface CreatePhoneCallLogInput {
  phone_number: string
  direction: 'incoming' | 'outbound'
  caller_name?: string | null
  notes?: string | null
  customer_id?: string | null
  property_id?: string | null
  contract_id?: string | null
  job_id?: string | null
}

const SELECT_FIELDS =
  'id, call_id, phone_number, direction, duration_seconds, status, caller_name, line_number, notes, customer_id, property_id, contract_id, job_id, lead_id, proposal_id, conversation_id, assigned_to_user_id, assigned_at, handling_status, waiting_until, priority, queue_id, sla_due_at, state, source, created_by, created_at, updated_at'

export async function fetchPhoneCallLogs(limit = 200): Promise<PhoneCallLog[]> {
  const { data, error } = await supabase
    .from('phone_call_logs')
    .select(SELECT_FIELDS)
    .order('created_at', { ascending: false })
    .limit(limit)
  if (error) throw supabaseError(error, 'Failed to load call logs')
  return (data || []) as PhoneCallLog[]
}

export async function createPhoneCallLog(input: CreatePhoneCallLogInput): Promise<PhoneCallLog> {
  const { data, error } = await supabase
    .from('phone_call_logs')
    .insert({
      phone_number: input.phone_number,
      direction: input.direction,
      caller_name: input.caller_name || null,
      notes: input.notes || null,
      customer_id: input.customer_id || null,
      property_id: input.property_id || null,
      contract_id: input.contract_id || null,
      job_id: input.job_id || null,
      source: 'manual',
    })
    .select(SELECT_FIELDS)
    .single()
  if (error) throw supabaseError(error, 'Failed to create call log')
  return data as PhoneCallLog
}

export async function updatePhoneCallLog(id: string, patch: PhoneCallLogUpdate): Promise<PhoneCallLog> {
  const { data, error } = await supabase
    .from('phone_call_logs')
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select(SELECT_FIELDS)
    .single()
  if (error) throw supabaseError(error, 'Failed to save call notes')
  return data as PhoneCallLog
}
